import React, { useEffect, useState } from 'react';
import { Card, Statistic, Row, Col, Spin, Empty, Button, Tag } from 'antd';
import { ThunderboltOutlined, FileTextOutlined } from '@ant-design/icons';
import AppLayout from '../components/AppLayout';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

const TYPE_META = {
  question: { label: 'Questions',       color: '#0958d9' },
  solution: { label: 'Worked Solutions', color: '#389e0d' },
  notes:    { label: 'Summary Notes',    color: '#531dab' },
  paper:    { label: 'Exam Papers',      color: '#c41d7f' },
};

export default function AIUsage() {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/ai/usage')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const maxCount = data?.breakdown?.length
    ? Math.max(...data.breakdown.map(b => b.count))
    : 1;

  return (
    <AppLayout>
      <div className="page-header">
        <h1>AI Usage</h1>
        <p>Track your AI content generation activity.</p>
      </div>

      <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: '10px 16px', marginBottom: 24, fontSize: 13, color: '#874d00' }}>
        <ThunderboltOutlined style={{ marginRight: 8 }} />
        AI generation is currently <strong>free during beta</strong>. Usage limits may apply in future.
      </div>

      {loading && <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>}

      {!loading && data?.totals?.total_generations === 0 && (
        <Empty
          description="You haven't generated any content yet."
          style={{ padding: 80 }}
        >
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            style={{ background: '#0a0a0a', border: 'none' }}
            onClick={() => navigate('/material')}
          >
            Generate your first question
          </Button>
        </Empty>
      )}

      {!loading && data?.totals?.total_generations > 0 && (
        <>
          {/* Totals */}
          <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card variant="outlined" style={{ borderColor: '#e8e8e8' }}>
                <Statistic
                  title="Total generations"
                  value={data.totals.total_generations}
                  prefix={<ThunderboltOutlined />}
                  valueStyle={{ color: '#0a0a0a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card variant="outlined" style={{ borderColor: '#e8e8e8' }}>
                <Statistic
                  title="Total tokens used"
                  value={data.totals.total_tokens?.toLocaleString()}
                  prefix={<FileTextOutlined />}
                  valueStyle={{ color: '#0a0a0a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card variant="outlined" style={{ borderColor: '#e8e8e8' }}>
                <Statistic
                  title="Avg tokens / generation"
                  value={data.totals.total_generations
                    ? Math.round(data.totals.total_tokens / data.totals.total_generations).toLocaleString()
                    : 0}
                  valueStyle={{ color: '#0a0a0a' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Breakdown */}
          <Card variant="outlined" style={{ borderColor: '#e8e8e8' }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 20 }}>Breakdown by type</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {data.breakdown.map(b => {
                const meta = TYPE_META[b.generation_type] || { label: b.generation_type, color: '#888' };
                const pct  = Math.round((b.count / maxCount) * 100);
                return (
                  <div key={b.generation_type}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag color={meta.color} style={{ margin: 0 }}>{meta.label}</Tag>
                      </div>
                      <div style={{ fontSize: 13, color: '#555' }}>
                        <strong>{b.count}</strong> generation{b.count !== 1 ? 's' : ''}
                        <span style={{ color: '#aaa', marginLeft: 8 }}>· {b.total_tokens?.toLocaleString()} tokens</span>
                      </div>
                    </div>
                    <div style={{ height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${pct}%`,
                        background: meta.color, borderRadius: 4,
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </AppLayout>
  );
}
